// apps/dylan/src/anki.js — the AnkiConnect adapter. CLIENT-SIDE by design: the browser on
// Dylan's laptop talks to Anki Desktop's AnkiConnect add-on at http://localhost:8765 — the
// droplet NEVER calls Anki (localhost is exempt from mixed-content blocking on an https page;
// AnkiConnect's webCorsOriginList must include https://dylan.school-app.net — the UI's setup
// instructions state both). The server's only Anki role is bookkeeping the exported flags.
//
// Every failure mode returns a SHAPED result ({ ok: false, reason, … }) — this module never
// throws into the UI. Transport rides an injectable fetch so tests run against fakes.

import { renderMarkdownSubset } from "@school-apps/core/lib/markdown"; // C·5: Anki fields are HTML-native
import { isCloze } from "@school-apps/core/lib/cloze"; // CZ·3: cloze sources map to Anki's native Cloze note type
// IMG·4: the SAME cap the server enforces on attach — one owner, from a client-safe module.
import { CARD_IMAGE_MAX_BYTES, IMAGE_EXTENSIONS } from "@school-apps/core/images/limits";

export const ANKI_URL = "http://localhost:8765";
export const ANKI_VERSION = 6; // the AnkiConnect API version this adapter speaks
// One named deck, created if absent — his cards sort together in Anki and the course-level
// tags carry the finer grain (Anki tags are space-separated, so spaces become underscores per
// Anki convention). Named "Anki" by owner decision (Rev 58, replacing "School Companion" —
// the deck name is a direct tie to the product it lives in).
export const ANKI_DECK = "Anki";

/**
 * IMG·4 — the media filename for a card's image. DETERMINISTIC and namespaced by the app: pushing
 * the same card twice overwrites one file instead of littering his collection.media with copies,
 * and the prefix means nothing of his own is ever at risk of being clobbered.
 */
export const mediaFilenameFor = (cardId, mediaType) =>
  `school-apps-${String(cardId).replace(/[^A-Za-z0-9_-]/g, "")}.${IMAGE_EXTENSIONS[mediaType] ?? "jpg"}`;

/** Split a stored data URI into its media type + base64 payload, or null if it is not one. */
function splitDataUri(dataUri) {
  const m = /^data:([a-z]+\/[a-z0-9+.-]+);base64,([A-Za-z0-9+/]+={0,2})$/i.exec(String(dataUri ?? ""));
  if (!m) return null;
  const b64 = m[2];
  const pad = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return { mediaType: m[1].toLowerCase(), b64, bytes: Math.floor((b64.length * 3) / 4) - pad };
}

/**
 * Build an AnkiConnect client.
 * @param {{ fetch?: typeof globalThis.fetch, url?: string }} [io] injectable transport (tests pass fakes)
 */
export function createAnkiClient({ fetch, url = ANKI_URL } = {}) {
  // bind the real fetch to its global — an unbound window.fetch throws "Illegal invocation"
  const doFetch = fetch ?? (globalThis.fetch ? globalThis.fetch.bind(globalThis) : undefined);
  /**
   * One AnkiConnect call: POST { action, version, params } -> { result, error }.
   * @returns {Promise<{ ok: true, result: any } | { ok: false, reason: string, error?: string }>}
   */
  async function invoke(action, params = {}) {
    let res;
    try {
      res = await doFetch(url, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action, version: ANKI_VERSION, params }),
      });
    } catch {
      return { ok: false, reason: "unreachable" }; // Anki closed / add-on missing / wrong machine
    }
    if (!res || !res.ok) return { ok: false, reason: "unreachable" };
    let data;
    try { data = await res.json(); } catch { return { ok: false, reason: "bad-response" }; }
    if (data && data.error != null) return { ok: false, reason: "anki-error", error: String(data.error) };
    return { ok: true, result: data ? data.result : undefined };
  }

  /** The version handshake: proves AnkiConnect answers and speaks at least our API version. */
  async function hello() {
    const r = await invoke("version");
    if (!r.ok) return r;
    if (typeof r.result !== "number" || r.result < ANKI_VERSION) {
      return { ok: false, reason: "version", version: r.result };
    }
    return { ok: true, version: r.result };
  }

  /** Create the deck if absent (AnkiConnect's createDeck is idempotent). */
  async function ensureDeck(deck = ANKI_DECK) {
    const r = await invoke("createDeck", { deck });
    return r.ok ? { ok: true } : r;
  }

  /**
   * Push cards as Basic notes (Front/Back) — front/back mapped through the C·5 markdown renderer at
   * export (Anki fields are HTML-native, so his cards read the same in Anki as in the app). Tags
   * carried (spaces -> underscores). AnkiConnect
   * returns one note id per note, null where a note wasn't added (usually a duplicate) — those
   * card ids come back as `skipped` and STAY pending (only confirmed adds flip the flag).
   * @param {Array<{ id: string, front: string, back: string, tags?: string[] }>} cards
   * @param {string} [deck]
   * @returns {Promise<{ ok: true, added: string[], skipped: string[] } | { ok: false, reason: string, error?: string }>}
   */
  /**
   * IMG·4 — store one card's image in Anki's media folder, returning the filename to reference from
   * the note. Returns null when there is no image, when the payload is not a well-formed data URI,
   * when it exceeds the cap the server itself enforces, or when AnkiConnect refuses it: in every one
   * of those cases the note still goes, WITHOUT the picture. That is the honest trade — the card's
   * text is what he studies, the image is a cue, and blocking a whole export over a failed thumbnail
   * would cost him the study material to save the decoration.
   */
  async function storeCardMedia(card) {
    const parsed = splitDataUri(card?.image?.dataUri);
    if (!parsed) return null;
    if (parsed.bytes > CARD_IMAGE_MAX_BYTES) return null; // the same ceiling the attach route enforces
    const filename = mediaFilenameFor(card.id, parsed.mediaType);
    const r = await invoke("storeMediaFile", { filename, data: parsed.b64 });
    if (!r.ok) return null;
    // ANK·1 — REFERENCE THE NAME ANKI ACTUALLY WROTE. `storeMediaFile` answers with the stored
    // filename, and Anki's media layer does NOT promise it is the one we asked for: a same-name file
    // with different bytes is written under a new name instead of being clobbered. Building the note
    // from our REQUESTED name after such a rename ships `<img src="…">` pointing at a file that is
    // not there — the note arrives and the picture does not, silently, which is the field report
    // that opened this lesson. Every fake in this suite echoed the name back, so nothing caught it.
    // A non-string answer is not a failure (the file was written); we simply fall back to what we
    // asked for, which is our best knowledge of where it went.
    return typeof r.result === "string" && r.result.trim() ? r.result.trim() : filename;
  }

  /**
   * Push cards as Anki notes. IMG·4: a card carrying an image stores that image as a media file
   * FIRST, then references it from the note's front field — `<img src="…">` above the question,
   * matching where the app itself puts it. A card with NO image produces a byte-identical note to
   * the one this adapter has always produced (proven by a regression test), so nothing about his
   * existing pipeline moved.
   */
  async function addNotes(cards, deck = ANKI_DECK) {
    // media first, one call per image-bearing card; a failure yields null and the note goes plain
    const mediaNames = [];
    const mediaFailed = [];
    for (const c of cards) {
      const name = await storeCardMedia(c);
      mediaNames.push(name);
      if (!name && c?.image?.dataUri) mediaFailed.push(c.id); // it HAD one and we could not store it
    }
    const imgTag = (i) => (mediaNames[i] ? `<img src="${mediaNames[i]}">` : "");
    const notes = cards.map((c, i) => {
      const tags = (Array.isArray(c.tags) ? c.tags : []).map((t) => String(t).trim().replace(/\s+/g, "_")).filter(Boolean);
      // CZ·3 — a cloze SOURCE ships as Anki's native Cloze note: the {{cN::…}} markers ride the
      // Text field VERBATIM (the markdown subset escapes HTML, never braces); Anki does its own
      // sibling expansion. Everything else stays a Basic note, byte-identical to before.
      if (isCloze(c.front)) {
        return {
          deckName: deck,
          modelName: "Cloze",
          fields: { Text: imgTag(i) + renderMarkdownSubset(c.front), "Back Extra": renderMarkdownSubset(c.back ?? "") },
          tags,
          options: { allowDuplicate: false },
        };
      }
      return {
        deckName: deck,
        modelName: "Basic",
        fields: { Front: imgTag(i) + renderMarkdownSubset(c.front), Back: renderMarkdownSubset(c.back) },
        tags,
        options: { allowDuplicate: false },
      };
    });
    const r = await invoke("addNotes", { notes });
    if (!r.ok) return r;
    const ids = Array.isArray(r.result) ? r.result : [];
    const out = {
      ok: true,
      added: cards.filter((_, i) => ids[i] != null).map((c) => c.id),
      skipped: cards.filter((_, i) => ids[i] == null).map((c) => c.id),
    };
    // only present when something actually failed — an export with no images is byte-identical
    if (mediaFailed.length) out.mediaFailed = mediaFailed;
    return out;
  }

  /** Today's review count from Anki — the dashboard stat ("did I study?"). */
  async function reviewedToday() {
    const r = await invoke("getNumCardsReviewedToday");
    if (!r.ok) return r;
    return { ok: true, count: typeof r.result === "number" ? r.result : 0 };
  }

  return { invoke, hello, ensureDeck, addNotes, reviewedToday };
}
